import { useState, useRef } from 'react';
import { Monitor, Smartphone, Save, Loader2, X } from 'lucide-react';
import WebAccessPage, { type WebAccessActions } from './WebAccessPage';
import MobileAccessTab from './MobileAccessTab';
import { rolesAPI, type UniformCheckEngine } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { UniformCheckEngineChoice, useUniformCheckOptions } from '../components/EditRoleModal';

const TABS = [
  { key: 'web', label: 'Web Access', icon: Monitor },
  { key: 'mobile', label: 'Mobile Access', icon: Smartphone },
] as const;

type TabKey = (typeof TABS)[number]['key'];


export default function AccessManagementPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('web');
  const roleRef = useRef<WebAccessActions>(null);
  const [, forceUpdate] = useState(0);
  const { selectedProjectId } = useAuthStore();

  // Add Role modal state
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [newRoleCategory, setNewRoleCategory] = useState('L1');
  const [newRoleGeoBased, setNewRoleGeoBased] = useState(false);
  const [newRoleAwsVerification, setNewRoleAwsVerification] = useState(false);
  // roles.uniform_check_engine — who checks uniform & background; sent only with the toggle above on.
  const [newRoleUniformCheckEngine, setNewRoleUniformCheckEngine] = useState<UniformCheckEngine>('gemini');
  // roles.multi_store_deployment — employees in the role may be mapped to several stores.
  const [newRoleMultiStore, setNewRoleMultiStore] = useState(false);
  // false = 'hierarchy' (sees only their reporting team), true = 'all_project'
  const [newRoleAllProject, setNewRoleAllProject] = useState(false);
  const [addingRole, setAddingRole] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // Whether Jev can be picked for this project: asked each time the Add Role modal opens.
  const uniformCheckOptions = useUniformCheckOptions(selectedProjectId, showAddRoleModal);

  const closeModal = () => {
    setShowAddRoleModal(false);
    setNewRoleName('');
    setNewRoleDescription('');
    setNewRoleCategory('L1');
    setNewRoleGeoBased(false);
    setNewRoleAwsVerification(false);
    setNewRoleUniformCheckEngine('gemini');
    setNewRoleMultiStore(false);
    setNewRoleAllProject(false);
  };

  const handleAddRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoleName.trim()) {
      alert('Role name is required');
      return;
    }
    setAddingRole(true);
    try {
      await rolesAPI.create({
        name: newRoleName,
        description: newRoleDescription,
        permissions: [],
        project_id: selectedProjectId ?? undefined,
        category: newRoleCategory,
        geo_based_attendance: newRoleGeoBased,
        aws_face_verification: newRoleAwsVerification,
        ...(newRoleAwsVerification ? { uniform_check_engine: newRoleUniformCheckEngine } : {}),
        multi_store_deployment: newRoleMultiStore,
        data_scope: newRoleAllProject ? 'all_project' : 'hierarchy',
      });
      closeModal();
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      console.error('Error creating role:', err);
      const errorMessage = err.response?.data?.message || 'Failed to create role';
      alert(errorMessage);
    } finally {
      setAddingRole(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Access Management</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage role-based menu access for web and mobile feature access for each budget
        </p>
      </div>

      {/* Tabs + Add Role */}
      <div className="flex items-end justify-between border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors
                  ${isActive
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
              >
                <Icon className={`h-4 w-4 ${isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}`} />
                {tab.label}
              </button>
            );
          })}
        </nav>
        <button
          onClick={() => setShowAddRoleModal(true)}
          className="inline-flex items-center px-4 py-2 mb-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
        >
          Add Role
        </button>
      </div>

      {/* Action bar + Tab Content */}
      {activeTab === 'web' && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Toggle menu access for each role. A page’s tick also gives what its grey line
              says. Changes apply when you save.
            </p>
            <button
              onClick={() => { roleRef.current?.onSave(); setTimeout(() => forceUpdate((n) => n + 1), 100); }}
              disabled={roleRef.current?.saving || !roleRef.current?.hasChanges}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {roleRef.current?.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          </div>
          <WebAccessPage ref={roleRef} onChangeNotify={() => forceUpdate((n) => n + 1)} refreshKey={refreshKey} />
        </>
      )}

      {activeTab === 'mobile' && <MobileAccessTab refreshKey={refreshKey} />}

      {/* Add Role Modal */}
      {showAddRoleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="bg-primary-600 rounded-t-xl px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Add New Role</h2>
              <button
                onClick={closeModal}
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddRole} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Role Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Admin, Manager, Viewer"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Description
                </label>
                <textarea
                  value={newRoleDescription}
                  onChange={(e) => setNewRoleDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  placeholder="Brief description of this role"
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <label className="text-sm font-medium text-gray-700">Geo-based Attendance</label>
                  <p className="text-xs text-gray-500">Sign in at any store marks daily attendance</p>
                </div>
                <button
                  type="button"
                  onClick={() => setNewRoleGeoBased(!newRoleGeoBased)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${newRoleGeoBased ? 'bg-primary-600' : 'bg-gray-200'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${newRoleGeoBased ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <div>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <label className="text-sm font-medium text-gray-700">Face Verification &amp; Uniform Compliance</label>
                    <p className="text-xs text-gray-500">AWS face match, and the uniform &amp; background check, during attendance</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewRoleAwsVerification(!newRoleAwsVerification)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${newRoleAwsVerification ? 'bg-primary-600' : 'bg-gray-200'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${newRoleAwsVerification ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                {newRoleAwsVerification && (
                  <UniformCheckEngineChoice
                    value={newRoleUniformCheckEngine}
                    onChange={setNewRoleUniformCheckEngine}
                    options={uniformCheckOptions}
                  />
                )}
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <label className="text-sm font-medium text-gray-700">Multi-store Deployment</label>
                  <p className="text-xs text-gray-500">Can be mapped to several stores at once (roaming promoters)</p>
                </div>
                <button
                  type="button"
                  onClick={() => setNewRoleMultiStore(!newRoleMultiStore)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${newRoleMultiStore ? 'bg-primary-600' : 'bg-gray-200'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${newRoleMultiStore ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <label className="text-sm font-medium text-gray-700">Project-wide data access</label>
                  <p className="text-xs text-gray-500">On: sees all employees in the project (reports). Off: only their reporting hierarchy.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setNewRoleAllProject(!newRoleAllProject)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${newRoleAllProject ? 'bg-primary-600' : 'bg-gray-200'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${newRoleAllProject ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingRole}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {addingRole && <Loader2 className="w-4 h-4 animate-spin" />}
                  Create Role
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
